import React, { useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { ItemWithEntryPointPopup } from '@atlassian/jira-item-with-popup/src/ui/item-with-entrypoint-popup/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { confluencePages_issueFieldConfluencePagesReadviewFull_ConfluencePagesReadView$key as ConfluencePagesFragment } from './__generated__/confluencePages_issueFieldConfluencePagesReadviewFull_ConfluencePagesReadView.graphql';
import type { ConfluencePagesReadViewProps } from './types.tsx';
import { confluencePagesPopupContentEntryPoint } from './popup-content/entrypoint.tsx';
import messages from './messages.tsx';
import type { Props as PopupContentProps } from './popup-content/types.tsx';

/**
 * The ConfluencePagesReadView will show a read only view of ConfluencePages field.
 * @param props [ConfluencePagesReadViewProps](./types.tsx)
 */
export const ConfluencePagesReadView = ({
	fragmentRef,
	isTruncated = false,
}: ConfluencePagesReadViewProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { confluenceRemoteIssueLinks } = useFragment<ConfluencePagesFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment confluencePages_issueFieldConfluencePagesReadviewFull_ConfluencePagesReadView on JiraConfluenceRemoteIssueLinksField
			@argumentDefinitions(
				isJiraConfluencePageContentEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-confluence-page-content-enabled.relayprovider"
				}
			) {
				confluenceRemoteIssueLinks(first: 1000) @optIn(to: "JiraConfluenceRemoteIssueLinksField") {
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...popupContent_issueFieldConfluencePagesReadviewFull
					totalCount
					edges {
						node {
							href
							pageContent @include(if: $isJiraConfluencePageContentEnabled) {
								... on JiraConfluencePageContentDetails {
									href
								}
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const totalCount = confluenceRemoteIssueLinks?.totalCount ?? 0;
	const node = confluenceRemoteIssueLinks?.edges?.[0]?.node;
	const popupContentProps: PopupContentProps | null = useMemo(
		() =>
			confluenceRemoteIssueLinks
				? {
						fragmentRef: confluenceRemoteIssueLinks,
					}
				: null,
		[confluenceRemoteIssueLinks],
	);

	if (!popupContentProps || !node || totalCount === 0) {
		return null;
	}

	const url = fg('jira_preview_panel_m_1_rollout')
		? (node.pageContent?.href ?? node.href ?? '')
		: (node.href ?? '');
	return (
		<ItemWithEntryPointPopup
			ItemComponent={<SimpleInlineSmartLink anchorTarget="_blank" url={url} />}
			totalItems={Math.min(totalCount, 1000)}
			entryPoint={confluencePagesPopupContentEntryPoint}
			entryPointParams={{}}
			entryPointRuntimeProps={popupContentProps}
			showMoreTooltip={formatMessage(messages.showMore)}
			actionSubjectIdPrefix="confluencePages"
			isTruncated={isTruncated}
		/>
	);
};
